import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getRunbooks,
  getRunbookProgress,
  toggleStepCompletion,
  notifyProgressChange,
  subscribeToProgress,
  type Runbook,
  type RunbookStep,
} from '../services/RunbookService';
import './RunbookPanel.css';

interface RunbookPanelProps {
  isOpen: boolean;
  onClose: () => void;
  initialRunbookId?: string;
}

export default function RunbookPanel({ isOpen, onClose, initialRunbookId }: RunbookPanelProps) {
  const navigate = useNavigate();
  const [runbooks, setRunbooks] = useState<Runbook[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(initialRunbookId || null);
  const [filter, setFilter] = useState<'all' | 'getting-started' | 'deployment' | 'maintenance' | 'incident'>('all');
  
  // Load runbooks
  useEffect(() => {
    const loadRunbooks = () => setRunbooks(getRunbooks());
    loadRunbooks();
    
    // Subscribe to progress changes
    const unsubscribe = subscribeToProgress(loadRunbooks);
    return unsubscribe;
  }, []);
  
  // Set initial expanded runbook
  useEffect(() => {
    if (initialRunbookId) {
      setExpandedId(initialRunbookId);
    }
  }, [initialRunbookId]);
  
  const handleStepToggle = (stepId: string) => {
    toggleStepCompletion(stepId);
    notifyProgressChange();
    setRunbooks(getRunbooks());
  };
  
  const handleStepClick = (step: RunbookStep) => {
    if (step.link) {
      navigate(step.link);
      onClose();
    }
  };
  
  const getCategoryIcon = (category: Runbook['category']) => {
    switch (category) {
      case 'getting-started': return '🚀';
      case 'deployment': return '📦';
      case 'maintenance': return '🔧';
      case 'incident': return '🚨';
    }
  };
  
  const getCategoryLabel = (category: Runbook['category']) => {
    switch (category) {
      case 'getting-started': return 'Getting Started';
      case 'deployment': return 'Deployment';
      case 'maintenance': return 'Maintenance';
      case 'incident': return 'Incident Response';
    }
  };
  
  const filteredRunbooks = filter === 'all' 
    ? runbooks 
    : runbooks.filter(r => r.category === filter);
  
  if (!isOpen) return null;
  
  return (
    <div className="runbook-overlay" onClick={onClose}>
      <div className="runbook-panel" onClick={e => e.stopPropagation()}>
        <div className="runbook-header">
          <div className="header-content">
            <h2>Operator Runbooks</h2>
            <p className="header-subtitle">Step-by-step guides for common operations</p>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        {/* Category Filter */}
        <div className="category-filter">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button 
            className={`filter-btn ${filter === 'getting-started' ? 'active' : ''}`}
            onClick={() => setFilter('getting-started')}
          >
            🚀 Getting Started
          </button>
          <button 
            className={`filter-btn ${filter === 'deployment' ? 'active' : ''}`}
            onClick={() => setFilter('deployment')}
          >
            📦 Deployment
          </button>
          <button 
            className={`filter-btn ${filter === 'maintenance' ? 'active' : ''}`}
            onClick={() => setFilter('maintenance')}
          >
            🔧 Maintenance
          </button>
          <button 
            className={`filter-btn ${filter === 'incident' ? 'active' : ''}`}
            onClick={() => setFilter('incident')}
          >
            🚨 Incident
          </button>
        </div>
        
        {/* Runbooks List */}
        <div className="runbooks-list">
          {filteredRunbooks.map(runbook => {
            const progress = getRunbookProgress(runbook.id);
            const isExpanded = expandedId === runbook.id;
            const completedSteps = runbook.steps.filter(s => s.completed).length;
            
            return (
              <div key={runbook.id} className={`runbook-card ${isExpanded ? 'expanded' : ''}`}>
                <div 
                  className="runbook-summary"
                  onClick={() => setExpandedId(isExpanded ? null : runbook.id)}
                >
                  <div className="runbook-icon">{getCategoryIcon(runbook.category)}</div>
                  <div className="runbook-info">
                    <h3 className="runbook-title">{runbook.title}</h3>
                    <p className="runbook-description">{runbook.description}</p>
                    <div className="runbook-meta">
                      <span className="category-badge">{getCategoryLabel(runbook.category)}</span>
                      <span className="time-estimate">⏱ {runbook.estimatedTime}</span>
                    </div>
                  </div>
                  <div className="runbook-progress">
                    <div className="progress-ring">
                      <svg viewBox="0 0 36 36">
                        <circle className="progress-bg" cx="18" cy="18" r="16" />
                        <circle 
                          className="progress-fill" 
                          cx="18" cy="18" r="16"
                          strokeDasharray={`${progress} 100`}
                        />
                      </svg>
                      <span className="progress-text">{progress}%</span>
                    </div>
                    <span className="step-count">{completedSteps}/{runbook.steps.length}</span>
                  </div>
                  <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                </div>
                
                {isExpanded && (
                  <div className="runbook-steps">
                    {runbook.steps.map((step, index) => (
                      <div key={step.id} className={`step-item ${step.completed ? 'completed' : ''}`}>
                        <button 
                          className="step-checkbox"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStepToggle(step.id);
                          }}
                        >
                          {step.completed ? '✓' : index + 1}
                        </button>
                        <div className="step-content">
                          <span className="step-title">{step.title}</span>
                          <span className="step-description">{step.description}</span>
                        </div>
                        {step.link && (
                          <button 
                            className="step-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStepClick(step);
                            }}
                          >
                            Go →
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Contextual Help Button component for embedding in pages
interface ContextualHelpProps {
  stepId: string;
  runbookId: string;
  onOpenRunbook?: (runbookId: string) => void;
}

export function ContextualHelpButton({ stepId, runbookId, onOpenRunbook }: ContextualHelpProps) {
  const runbooks = getRunbooks();
  const runbook = runbooks.find(r => r.id === runbookId);
  const step = runbook?.steps.find(s => s.id === stepId);
  
  if (!step) return null;
  
  return (
    <button 
      className="contextual-help-btn"
      onClick={() => onOpenRunbook?.(runbookId)}
      title={`Runbook: ${step.title}`}
    >
      ?
    </button>
  );
}
